import React, { useState, useEffect, useRef } from 'react';
import { X, Palette, MessageSquare, MessageCircle, Bot, Headphones, User, Eye, CheckCircle, Clock, XCircle, ClipboardList, Plus, Edit2, Trash2, Check, LogOut, Upload, Loader2, Sparkles, ImagePlus } from 'lucide-react';
import api from '../api';
import Toast from './Toast';
import {
    DEFAULT_CHAT_FORM_FIELDS,
    DEFAULT_CHAT_FORM_DISCLAIMER,
    CHAT_FORM_FIELD_TYPES,
    sanitizeChatFormFieldName,
    chatFormFieldsToPayload,
} from './chatEmbedFormUtils';
import { ChatImageSharingSetting } from './chatImageAttachments';

// Color presets for quick selection
const COLOR_PRESETS = [
    { name: 'Indigo', value: '#6366f1', preview: 'bg-indigo-500' },
    { name: 'Blue', value: '#3b82f6', preview: 'bg-blue-500' },
    { name: 'Purple', value: '#a855f7', preview: 'bg-purple-500' },
    { name: 'Pink', value: '#ec4899', preview: 'bg-pink-500' },
    { name: 'Red', value: '#ef4444', preview: 'bg-red-500' },
    { name: 'Orange', value: '#f97316', preview: 'bg-orange-500' },
    { name: 'Green', value: '#10b981', preview: 'bg-green-500' },
    { name: 'Teal', value: '#14b8a6', preview: 'bg-teal-500' },
    { name: 'Cyan', value: '#06b6d4', preview: 'bg-cyan-500' },
    { name: 'Gray', value: '#6b7280', preview: 'bg-gray-500' },
];

// Icon options
const ICON_OPTIONS = [
    { name: 'Message Square', value: 'message-square', Icon: MessageSquare },
    { name: 'Message Circle', value: 'message-circle', Icon: MessageCircle },
    { name: 'Bot', value: 'bot', Icon: Bot },
    { name: 'Headphones', value: 'headphones', Icon: Headphones },
    { name: 'User', value: 'user', Icon: User },
];

// Position options
const POSITION_OPTIONS = [
    { name: 'Bottom Right', value: 'bottom-right' },
    { name: 'Bottom Left', value: 'bottom-left' },
    { name: 'Top Right', value: 'top-right' },
    { name: 'Top Left', value: 'top-left' },
];

// Launcher size options (px values match ComboWidgetV2's launcher size scale)
const BUTTON_SIZE_OPTIONS = [
    { name: 'Small', value: 'small', px: 44 },
    { name: 'Medium', value: 'medium', px: 56 },
    { name: 'Large', value: 'large', px: 68 },
];

const ChatThemeCustomizer = ({ agent, onClose, onSave }) => {
    const [activeTab, setActiveTab] = useState('theme');
    const [themeSettings, setThemeSettings] = useState({
        chat_theme_mode: agent?.chat_theme_mode || 'light',
        chat_primary_color: agent?.chat_primary_color || '#6366f1',
        chat_button_icon: agent?.chat_button_icon || 'message-square',
        chat_button_color: agent?.chat_button_color || '#6366f1',
        chat_button_position: agent?.chat_button_position || 'bottom-right',
        chat_button_logo_url: agent?.chat_button_logo_url || null,
        chat_button_size: agent?.chat_button_size || 'medium',
        chat_teaser_enabled: agent?.chat_teaser_enabled || false,
        chat_teaser_message: agent?.chat_teaser_message || "Need help? We're online now.",
        chat_teaser_bg_color: agent?.chat_teaser_bg_color || '#ffffff',
        chat_teaser_text_color: agent?.chat_teaser_text_color || '#111827',
        chat_glow_enabled: agent?.chat_glow_enabled || false,
        chat_header_subtitle: agent?.chat_header_subtitle || '',
        chat_greeting_enabled: agent?.chat_greeting_enabled || false,
        chat_greeting_message: agent?.chat_greeting_message || '',
        chat_greeting_dynamic: agent?.chat_greeting_dynamic || false,
        chat_reminder_enabled: agent?.chat_reminder_enabled || false,
        chat_reminder_seconds: agent?.chat_reminder_seconds ?? 30,
        chat_reminder_type: agent?.chat_reminder_type || 'static',
        chat_reminder_message: agent?.chat_reminder_message || '',
        chat_auto_close_enabled: agent?.chat_auto_close_enabled || false,
        chat_auto_close_seconds: agent?.chat_auto_close_seconds ?? 30,
        chat_auto_close_message: agent?.chat_auto_close_message || '',
        chat_end_enabled: agent?.chat_end_enabled || false,
        chat_end_triggers: (() => {
            if (!agent?.chat_end_triggers) return '';
            try { return JSON.parse(agent.chat_end_triggers).join('\n'); } catch { return agent.chat_end_triggers; }
        })(),
        chat_end_confirm_message: agent?.chat_end_confirm_message || '',
        chat_end_close_message: agent?.chat_end_close_message || '',
        chat_image_upload_enabled: agent?.chat_image_upload_enabled || false,
        chat_embed_form_enabled: agent?.chat_embed_form_enabled || false,
        chat_embed_form_title: agent?.chat_embed_form_title || 'Before we chat',
        chat_embed_form_subtitle: agent?.chat_embed_form_subtitle || 'Please share your details so we can personalize your experience.',
        chat_embed_form_fields: Array.isArray(agent?.chat_embed_form_fields) && agent.chat_embed_form_fields.length > 0
            ? agent.chat_embed_form_fields
            : DEFAULT_CHAT_FORM_FIELDS,
        chat_embed_form_disclaimer: agent?.chat_embed_form_disclaimer ?? DEFAULT_CHAT_FORM_DISCLAIMER,
        chat_embed_form_skip_enabled: agent?.chat_embed_form_skip_enabled || false,
        chat_embed_form_skip_label: agent?.chat_embed_form_skip_label || 'Skip and chat',
    });
    const [editingFieldName, setEditingFieldName] = useState(null);
    const [editFieldDraft, setEditFieldDraft] = useState(null);
    const [newFieldLabel, setNewFieldLabel] = useState('');
    const [newFieldType, setNewFieldType] = useState('text');
    const [newFieldRequired, setNewFieldRequired] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [toast, setToast] = useState(null);
    const [showPreview, setShowPreview] = useState(false);
    const [isUploadingLogo, setIsUploadingLogo] = useState(false);
    const [logoUploadError, setLogoUploadError] = useState(null);
    const logoFileInputRef = useRef(null);

    // Get the icon component for the selected icon
    const getIconComponent = (iconValue) => {
        const icon = ICON_OPTIONS.find(opt => opt.value === iconValue);
        return icon ? icon.Icon : MessageSquare;
    };

    const handleLogoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/svg+xml'];
        if (!allowed.includes(file.type) && !file.name.match(/\.(jpg|jpeg|png|webp|gif|svg)$/i)) {
            setLogoUploadError('Please upload JPG, PNG, WebP, GIF, or SVG.');
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            setLogoUploadError('File too large. Maximum size is 5MB.');
            return;
        }

        setIsUploadingLogo(true);
        setLogoUploadError(null);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const response = await api.post(`/agents/${agent.id}/chat-embed/logo`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setThemeSettings((prev) => ({ ...prev, chat_button_logo_url: response.data.file_url }));
            if (logoFileInputRef.current) logoFileInputRef.current.value = '';
        } catch (err) {
            setLogoUploadError(err.response?.data?.detail || 'Failed to upload logo');
        } finally {
            setIsUploadingLogo(false);
        }
    };

    const handleLogoDelete = async () => {
        try {
            await api.delete(`/agents/${agent.id}/chat-embed/logo`);
            setThemeSettings((prev) => ({ ...prev, chat_button_logo_url: null }));
        } catch (err) {
            setLogoUploadError(err.response?.data?.detail || 'Failed to delete logo');
        }
    };

    const handleSave = async () => {
        if (themeSettings.chat_embed_form_enabled && (!themeSettings.chat_embed_form_fields || themeSettings.chat_embed_form_fields.length === 0)) {
            setToast({ message: 'Add at least one pre-chat form field', type: 'error' });
            return;
        }
        setIsSaving(true);
        try {
            // Convert trigger phrases textarea (one per line or comma-separated) to JSON array string
            let chatEndTriggersValue = null;
            if (themeSettings.chat_end_enabled && themeSettings.chat_end_triggers.trim()) {
                const phrases = themeSettings.chat_end_triggers
                    .split(/[\n,]/)
                    .map(s => s.trim().toLowerCase())
                    .filter(s => s.length > 0);
                chatEndTriggersValue = phrases.length > 0 ? JSON.stringify(phrases) : null;
            }
            const payload = {
                ...themeSettings,
                chat_end_triggers: chatEndTriggersValue,
                chat_end_confirm_message: themeSettings.chat_end_confirm_message.trim() || null,
                chat_end_close_message: themeSettings.chat_end_close_message.trim() || null,
                chat_embed_form_fields: chatFormFieldsToPayload(themeSettings.chat_embed_form_fields),
            };
            await api.put(`/agents/${agent.id}`, payload);
            setToast({ message: 'Chat widget settings saved successfully!', type: 'success' });
            setTimeout(() => {
                if (onSave) onSave(payload);
                onClose();
            }, 1000);
        } catch (err) {
            console.error('Failed to save chat widget settings:', err);
            setToast({ message: 'Failed to save chat widget settings', type: 'error' });
        } finally {
            setIsSaving(false);
        }
    };

    const updateFormFields = (fields) => {
        setThemeSettings((prev) => ({ ...prev, chat_embed_form_fields: fields }));
    };

    const handleAddFormField = () => {
        const label = newFieldLabel.trim();
        if (!label) return;
        const name = sanitizeChatFormFieldName(label);
        if (!name) return;
        if (themeSettings.chat_embed_form_fields.some((f) => f.name === name)) {
            setToast({ message: 'Field already exists', type: 'error' });
            return;
        }
        updateFormFields([
            ...themeSettings.chat_embed_form_fields,
            { name, label, type: newFieldType, required: newFieldRequired, placeholder: '' },
        ]);
        setNewFieldLabel('');
        setNewFieldType('text');
        setNewFieldRequired(true);
    };

    const startEditField = (field) => {
        setEditingFieldName(field.name);
        setEditFieldDraft({ ...field });
    };

    const saveEditField = () => {
        if (!editFieldDraft?.label?.trim()) return;
        const name = sanitizeChatFormFieldName(editFieldDraft.label);
        if (!name) return;
        if (themeSettings.chat_embed_form_fields.some((f) => f.name === name && f.name !== editingFieldName)) {
            setToast({ message: 'Field name already exists', type: 'error' });
            return;
        }
        updateFormFields(
            themeSettings.chat_embed_form_fields.map((f) =>
                f.name === editingFieldName
                    ? { ...editFieldDraft, name, label: editFieldDraft.label.trim() }
                    : f
            )
        );
        setEditingFieldName(null);
        setEditFieldDraft(null);
    };

    const removeFormField = (name) => {
        if (themeSettings.chat_embed_form_fields.length <= 1) {
            setToast({ message: 'At least one field is required when pre-chat form is enabled', type: 'error' });
            return;
        }
        updateFormFields(themeSettings.chat_embed_form_fields.filter((f) => f.name !== name));
    };

    const handlePreview = () => {
        // Open preview in a new tab with theme parameters
        const params = new URLSearchParams({
            view: 'button',
            theme: themeSettings.chat_theme_mode,
            primary: themeSettings.chat_primary_color.replace('#', ''),
            icon: themeSettings.chat_button_icon,
            btnColor: themeSettings.chat_button_color.replace('#', ''),
            position: themeSettings.chat_button_position,
        });
        const previewUrl = `${window.location.origin}/embed/chat/${agent.id}?${params.toString()}`;
        window.open(previewUrl, '_blank');
    };

    return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
            <div className="bg-card border border-border rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-elegant">
                {/* Header */}
                <div className="bg-gradient-secondary text-white p-5 flex justify-between items-center rounded-t-2xl">
                    <div className="flex items-center gap-3">
                        <div className="w-7 h-7 rounded-lg bg-white/20 flex items-center justify-center">
                            <Palette className="w-4 h-4" />
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold tracking-tight">Customize Chat Widget</h2>
                            <p className="text-white/70 text-xs mt-0.5 font-normal">Personalize your chat widget appearance</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-white/80 hover:text-white transition-colors p-1.5 rounded-lg hover:bg-white/10"
                        aria-label="Close"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Tabs */}
                <div className="flex gap-2 px-6 pt-4 border-b border-border">
                    <button
                        type="button"
                        onClick={() => setActiveTab('theme')}
                        className={`px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 transition-colors flex items-center gap-2 ${
                            activeTab === 'theme'
                                ? 'border-primary text-primary'
                                : 'border-transparent text-muted-foreground hover:text-foreground'
                        }`}
                    >
                        <Palette className="w-4 h-4" />
                        Theme
                    </button>
                    <button
                        type="button"
                        onClick={() => setActiveTab('prechat')}
                        className={`px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 transition-colors flex items-center gap-2 ${
                            activeTab === 'prechat'
                                ? 'border-primary text-primary'
                                : 'border-transparent text-muted-foreground hover:text-foreground'
                        }`}
                    >
                        <ClipboardList className="w-4 h-4" />
                        Pre-Chat Form
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                    {activeTab === 'prechat' ? (
                        <div className="space-y-6">
                            <div className="flex items-center gap-3">
                                <button
                                    type="button"
                                    role="switch"
                                    aria-checked={themeSettings.chat_embed_form_enabled}
                                    onClick={() => setThemeSettings({
                                        ...themeSettings,
                                        chat_embed_form_enabled: !themeSettings.chat_embed_form_enabled,
                                    })}
                                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                                        themeSettings.chat_embed_form_enabled ? 'bg-primary' : 'bg-muted'
                                    }`}
                                >
                                    <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                                        themeSettings.chat_embed_form_enabled ? 'translate-x-5' : 'translate-x-1'
                                    }`} />
                                </button>
                                <span className="text-sm text-foreground">
                                    {themeSettings.chat_embed_form_enabled ? 'Enabled' : 'Disabled'} – Require form before chat starts
                                </span>
                            </div>
                            <p className="text-xs text-muted-foreground">
                                Disabled by default. When enabled, visitors must submit the form before messaging.
                                Values are sent to the agent as <code className="text-primary">{`{{Variable_Name}}`}</code> in prompts.
                                Use the <strong>Chat Form Submitted</strong> custom function trigger for GHL contact lookup when the form is submitted,
                                so data is available before the greeting.
                            </p>

                            {themeSettings.chat_embed_form_enabled && (
                                <>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        <div className="space-y-1">
                                            <label className="text-xs font-medium text-foreground">Form title</label>
                                            <input
                                                type="text"
                                                value={themeSettings.chat_embed_form_title}
                                                onChange={(e) => setThemeSettings({ ...themeSettings, chat_embed_form_title: e.target.value })}
                                                className="w-full px-3 py-2 text-sm rounded-lg border-2 border-border bg-background"
                                            />
                                        </div>
                                        <div className="space-y-1 md:col-span-2">
                                            <label className="text-xs font-medium text-foreground">Form subtitle</label>
                                            <input
                                                type="text"
                                                value={themeSettings.chat_embed_form_subtitle}
                                                onChange={(e) => setThemeSettings({ ...themeSettings, chat_embed_form_subtitle: e.target.value })}
                                                className="w-full px-3 py-2 text-sm rounded-lg border-2 border-border bg-background"
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-1">
                                        <label className="text-xs font-medium text-foreground">Disclaimer message</label>
                                        <textarea
                                            value={themeSettings.chat_embed_form_disclaimer || ''}
                                            onChange={(e) => setThemeSettings({ ...themeSettings, chat_embed_form_disclaimer: e.target.value })}
                                            rows={3}
                                            placeholder="Leave empty to hide the disclaimer"
                                            className="w-full px-3 py-2 text-sm rounded-lg border-2 border-border bg-background resize-none"
                                        />
                                        <p className="text-xs text-muted-foreground">
                                            Shown below the form fields. Clear this field to hide the disclaimer.
                                        </p>
                                    </div>

                                    <div className="p-3 bg-muted/30 border border-border rounded-lg space-y-3">
                                        <div className="flex items-center gap-3">
                                            <button
                                                type="button"
                                                role="switch"
                                                aria-checked={themeSettings.chat_embed_form_skip_enabled}
                                                onClick={() => setThemeSettings({
                                                    ...themeSettings,
                                                    chat_embed_form_skip_enabled: !themeSettings.chat_embed_form_skip_enabled,
                                                })}
                                                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                                                    themeSettings.chat_embed_form_skip_enabled ? 'bg-primary' : 'bg-muted'
                                                }`}
                                            >
                                                <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                                                    themeSettings.chat_embed_form_skip_enabled ? 'translate-x-5' : 'translate-x-1'
                                                }`} />
                                            </button>
                                            <span className="text-sm text-foreground">Allow visitors to skip the form</span>
                                        </div>
                                        {themeSettings.chat_embed_form_skip_enabled && (
                                            <div className="space-y-1">
                                                <label className="text-xs font-medium text-foreground">Skip button label</label>
                                                <input
                                                    type="text"
                                                    value={themeSettings.chat_embed_form_skip_label}
                                                    onChange={(e) => setThemeSettings({ ...themeSettings, chat_embed_form_skip_label: e.target.value })}
                                                    placeholder="Skip and chat"
                                                    className="w-full px-3 py-2 text-sm rounded-lg border-2 border-border bg-background"
                                                />
                                            </div>
                                        )}
                                        <p className="text-xs text-muted-foreground">
                                            When enabled, visitors can start chatting without providing their information.
                                        </p>
                                    </div>

                                    <div className="space-y-3">
                                        <div className="flex items-center justify-between">
                                            <label className="text-sm font-semibold text-foreground">Form fields</label>
                                        </div>
                                        <div className="space-y-2">
                                            {themeSettings.chat_embed_form_fields.map((field) => (
                                                <div key={field.name} className="p-3 bg-muted/30 border border-border rounded-lg">
                                                    {editingFieldName === field.name && editFieldDraft ? (
                                                        <div className="space-y-2">
                                                            <input
                                                                type="text"
                                                                value={editFieldDraft.label}
                                                                onChange={(e) => setEditFieldDraft({ ...editFieldDraft, label: e.target.value })}
                                                                className="w-full px-3 py-1.5 text-sm rounded-lg border border-border bg-background"
                                                            />
                                                            <div className="flex flex-wrap gap-2">
                                                                <select
                                                                    value={editFieldDraft.type || 'text'}
                                                                    onChange={(e) => setEditFieldDraft({ ...editFieldDraft, type: e.target.value })}
                                                                    className="px-2 py-1.5 text-sm rounded-lg border border-border bg-background"
                                                                >
                                                                    {CHAT_FORM_FIELD_TYPES.map((t) => (
                                                                        <option key={t.value} value={t.value}>{t.label}</option>
                                                                    ))}
                                                                </select>
                                                                <label className="flex items-center gap-1.5 text-xs">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={editFieldDraft.required !== false}
                                                                        onChange={(e) => setEditFieldDraft({ ...editFieldDraft, required: e.target.checked })}
                                                                    />
                                                                    Required
                                                                </label>
                                                                <input
                                                                    type="text"
                                                                    value={editFieldDraft.placeholder || ''}
                                                                    onChange={(e) => setEditFieldDraft({ ...editFieldDraft, placeholder: e.target.value })}
                                                                    placeholder="Placeholder"
                                                                    className="flex-1 min-w-[120px] px-2 py-1.5 text-sm rounded-lg border border-border bg-background"
                                                                />
                                                            </div>
                                                            <div className="flex gap-1">
                                                                <button type="button" onClick={saveEditField} className="p-1.5 text-success hover:bg-success/10 rounded"><Check className="w-4 h-4" /></button>
                                                                <button type="button" onClick={() => { setEditingFieldName(null); setEditFieldDraft(null); }} className="p-1.5 hover:bg-muted rounded"><X className="w-4 h-4" /></button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="flex items-center gap-2">
                                                            <div className="flex-1">
                                                                <div className="text-sm font-medium">
                                                                    {field.label}
                                                                    {field.required !== false && <span className="text-destructive ml-1">*</span>}
                                                                </div>
                                                                <div className="text-xs text-muted-foreground font-mono mt-0.5">
                                                                    {field.name} · {field.type || 'text'} · {`{{${field.name}}}`}
                                                                </div>
                                                            </div>
                                                            <button type="button" onClick={() => startEditField(field)} className="p-1.5 hover:bg-muted rounded"><Edit2 className="w-4 h-4" /></button>
                                                            <button type="button" onClick={() => removeFormField(field.name)} className="p-1.5 text-destructive hover:bg-destructive/10 rounded"><Trash2 className="w-4 h-4" /></button>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                        <div className="flex flex-wrap gap-2 items-end p-3 border border-dashed border-border rounded-lg">
                                            <input
                                                type="text"
                                                value={newFieldLabel}
                                                onChange={(e) => setNewFieldLabel(e.target.value)}
                                                onKeyDown={(e) => e.key === 'Enter' && handleAddFormField()}
                                                placeholder="New field label"
                                                className="flex-1 min-w-[140px] px-3 py-2 text-sm rounded-lg border border-border bg-background"
                                            />
                                            <select
                                                value={newFieldType}
                                                onChange={(e) => setNewFieldType(e.target.value)}
                                                className="px-2 py-2 text-sm rounded-lg border border-border bg-background"
                                            >
                                                {CHAT_FORM_FIELD_TYPES.map((t) => (
                                                    <option key={t.value} value={t.value}>{t.label}</option>
                                                ))}
                                            </select>
                                            <label className="flex items-center gap-1.5 text-xs whitespace-nowrap">
                                                <input type="checkbox" checked={newFieldRequired} onChange={(e) => setNewFieldRequired(e.target.checked)} />
                                                Required
                                            </label>
                                            <button
                                                type="button"
                                                onClick={handleAddFormField}
                                                disabled={!newFieldLabel.trim()}
                                                className="px-3 py-2 bg-primary text-primary-foreground rounded-lg text-sm flex items-center gap-1 disabled:opacity-50"
                                            >
                                                <Plus className="w-4 h-4" />
                                                Add
                                            </button>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    ) : (
                    <>
                    {/* Theme Mode */}
                    <div className="space-y-3">
                        <label className="text-sm font-semibold text-foreground">Theme Mode</label>
                        <div className="grid grid-cols-3 gap-3">
                            {['light', 'dark', 'auto'].map((mode) => (
                                <button
                                    key={mode}
                                    onClick={() => setThemeSettings({ ...themeSettings, chat_theme_mode: mode })}
                                    className={`px-4 py-3 rounded-lg border-2 transition-all capitalize ${
                                        themeSettings.chat_theme_mode === mode
                                            ? 'border-primary bg-primary/10 text-primary font-semibold'
                                            : 'border-border hover:border-primary/50 text-foreground'
                                    }`}
                                >
                                    {mode}
                                </button>
                            ))}
                        </div>
                        <p className="text-xs text-muted-foreground">
                            Choose the theme mode for your chat widget. Auto mode will match the user's system preference.
                        </p>
                    </div>

                    {/* Primary Color */}
                    <div className="space-y-3">
                        <label className="text-sm font-semibold text-foreground">Primary Color</label>
                        <div className="grid grid-cols-5 gap-3">
                            {COLOR_PRESETS.map((color) => (
                                <button
                                    key={color.value}
                                    onClick={() => setThemeSettings({ 
                                        ...themeSettings, 
                                        chat_primary_color: color.value 
                                    })}
                                    className={`flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition-all ${
                                        themeSettings.chat_primary_color === color.value
                                            ? 'border-primary bg-primary/5'
                                            : 'border-border hover:border-primary/50'
                                    }`}
                                >
                                    <div 
                                        className="w-8 h-8 rounded-full" 
                                        style={{ backgroundColor: color.value }}
                                    />
                                    <span className="text-xs text-foreground">{color.name}</span>
                                    {themeSettings.chat_primary_color === color.value && (
                                        <CheckCircle className="w-4 h-4 text-primary" />
                                    )}
                                </button>
                            ))}
                        </div>
                        <div className="bg-gradient-to-br from-muted/40 to-muted/20 border-2 border-border/50 rounded-xl p-5 shadow-sm hover:shadow-md transition-all">
                            <div className="flex items-center gap-4">
                                <div className="flex flex-col gap-2">
                                    <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                                        Color Picker
                                    </label>
                                    <label 
                                        htmlFor="primary-color-picker"
                                        className="relative cursor-pointer group"
                                    >
                                        <div 
                                            className="w-24 h-12 rounded-xl border-3 border-white shadow-lg group-hover:shadow-xl group-hover:scale-105 transition-all duration-300 ring-2 ring-offset-2 ring-border group-hover:ring-primary"
                                            style={{ backgroundColor: themeSettings.chat_primary_color }}
                                        >
                                            <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-white/20 to-transparent pointer-events-none" />
                                        </div>
                                        <input
                                            id="primary-color-picker"
                                            type="color"
                                            value={themeSettings.chat_primary_color}
                                            onChange={(e) => setThemeSettings({ 
                                                ...themeSettings, 
                                                chat_primary_color: e.target.value 
                                            })}
                                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                        />
                                        <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-primary rounded-full shadow-md flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                            <Palette className="w-3 h-3 text-white" />
                                        </div>
                                    </label>
                                </div>
                                <div className="flex-1">
                                    <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 block">
                                        Hex Code
                                    </label>
                                    <div className="relative">
                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-mono text-sm pointer-events-none">
                                            #
                                        </span>
                                        <input
                                            type="text"
                                            value={themeSettings.chat_primary_color.replace('#', '')}
                                            onChange={(e) => {
                                                const value = '#' + e.target.value.replace('#', '');
                                                if (/^#[0-9A-Fa-f]{0,6}$/.test(value)) {
                                                    setThemeSettings({ 
                                                        ...themeSettings, 
                                                        chat_primary_color: value 
                                                    });
                                                }
                                            }}
                                            className="w-full pl-7 pr-3 py-3 text-sm font-mono font-semibold bg-background border-2 border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary shadow-sm transition-all uppercase"
                                            placeholder="6366f1"
                                            maxLength={6}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                                <div className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                                <p>Click the color box to open picker, or type hex code</p>
                            </div>
                        </div>
                    </div>

                    {/* Button Icon */}
                    <div className="space-y-3">
                        <label className="text-sm font-semibold text-foreground">Chat Button Icon</label>
                        <div className="grid grid-cols-5 gap-3">
                            {ICON_OPTIONS.map((icon) => (
                                <button
                                    key={icon.value}
                                    onClick={() => setThemeSettings({ 
                                        ...themeSettings, 
                                        chat_button_icon: icon.value 
                                    })}
                                    className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
                                        themeSettings.chat_button_icon === icon.value
                                            ? 'border-primary bg-primary/10 text-primary'
                                            : 'border-border hover:border-primary/50 text-foreground'
                                    }`}
                                >
                                    <icon.Icon className="w-6 h-6" />
                                    <span className="text-xs">{icon.name}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Button Color */}
                    <div className="space-y-3">
                        <label className="text-sm font-semibold text-foreground">Button Color</label>
                        <div className="grid grid-cols-5 gap-3">
                            {COLOR_PRESETS.map((color) => (
                                <button
                                    key={color.value}
                                    onClick={() => setThemeSettings({ 
                                        ...themeSettings, 
                                        chat_button_color: color.value 
                                    })}
                                    className={`flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition-all ${
                                        themeSettings.chat_button_color === color.value
                                            ? 'border-primary bg-primary/5'
                                            : 'border-border hover:border-primary/50'
                                    }`}
                                >
                                    <div 
                                        className="w-8 h-8 rounded-full" 
                                        style={{ backgroundColor: color.value }}
                                    />
                                    <span className="text-xs text-foreground">{color.name}</span>
                                    {themeSettings.chat_button_color === color.value && (
                                        <CheckCircle className="w-4 h-4 text-primary" />
                                    )}
                                </button>
                            ))}
                        </div>
                        <div className="bg-gradient-to-br from-muted/40 to-muted/20 border-2 border-border/50 rounded-xl p-5 shadow-sm hover:shadow-md transition-all">
                            <div className="flex items-center gap-4">
                                <div className="flex flex-col gap-2">
                                    <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                                        Color Picker
                                    </label>
                                    <label 
                                        htmlFor="button-color-picker"
                                        className="relative cursor-pointer group"
                                    >
                                        <div 
                                            className="w-24 h-12 rounded-xl border-3 border-white shadow-lg group-hover:shadow-xl group-hover:scale-105 transition-all duration-300 ring-2 ring-offset-2 ring-border group-hover:ring-primary"
                                            style={{ backgroundColor: themeSettings.chat_button_color }}
                                        >
                                            <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-white/20 to-transparent pointer-events-none" />
                                        </div>
                                        <input
                                            id="button-color-picker"
                                            type="color"
                                            value={themeSettings.chat_button_color}
                                            onChange={(e) => setThemeSettings({ 
                                                ...themeSettings, 
                                                chat_button_color: e.target.value 
                                            })}
                                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                        />
                                        <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-primary rounded-full shadow-md flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                            <Palette className="w-3 h-3 text-white" />
                                        </div>
                                    </label>
                                </div>
                                <div className="flex-1">
                                    <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 block">
                                        Hex Code
                                    </label>
                                    <div className="relative">
                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-mono text-sm pointer-events-none">
                                            #
                                        </span>
                                        <input
                                            type="text"
                                            value={themeSettings.chat_button_color.replace('#', '')}
                                            onChange={(e) => {
                                                const value = '#' + e.target.value.replace('#', '');
                                                if (/^#[0-9A-Fa-f]{0,6}$/.test(value)) {
                                                    setThemeSettings({ 
                                                        ...themeSettings, 
                                                        chat_button_color: value 
                                                    });
                                                }
                                            }}
                                            className="w-full pl-7 pr-3 py-3 text-sm font-mono font-semibold bg-background border-2 border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary shadow-sm transition-all uppercase"
                                            placeholder="6366f1"
                                            maxLength={6}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                                <div className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                                <p>Click the color box to open picker, or type hex code</p>
                            </div>
                        </div>
                    </div>

                    {/* Button Position */}
                    <div className="space-y-3">
                        <label className="text-sm font-semibold text-foreground">Button Position</label>
                        <div className="grid grid-cols-2 gap-3">
                            {POSITION_OPTIONS.map((position) => (
                                <button
                                    key={position.value}
                                    onClick={() => setThemeSettings({ 
                                        ...themeSettings, 
                                        chat_button_position: position.value 
                                    })}
                                    className={`px-4 py-3 rounded-lg border-2 transition-all ${
                                        themeSettings.chat_button_position === position.value
                                            ? 'border-primary bg-primary/10 text-primary font-semibold'
                                            : 'border-border hover:border-primary/50 text-foreground'
                                    }`}
                                >
                                    {position.name}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Launcher Logo */}
                    <div className="space-y-3">
                        <label className="text-sm font-semibold text-foreground">Launcher Logo</label>
                        <div className="flex items-center gap-3">
                            <input
                                ref={logoFileInputRef}
                                type="file"
                                accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml,.jpg,.jpeg,.png,.webp,.gif,.svg"
                                onChange={handleLogoUpload}
                                className="hidden"
                                id="chat-embed-logo-upload"
                            />
                            <label
                                htmlFor="chat-embed-logo-upload"
                                className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg flex items-center gap-2 text-sm cursor-pointer"
                            >
                                {isUploadingLogo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                                {isUploadingLogo ? 'Uploading…' : 'Upload Logo'}
                            </label>
                            {themeSettings.chat_button_logo_url && (
                                <button
                                    type="button"
                                    onClick={handleLogoDelete}
                                    className="px-3 py-2 text-destructive hover:bg-destructive/10 rounded-lg flex items-center gap-1.5 text-sm"
                                >
                                    <Trash2 className="w-4 h-4" />
                                    Remove
                                </button>
                            )}
                            {themeSettings.chat_button_logo_url && (
                                <img
                                    src={themeSettings.chat_button_logo_url}
                                    alt="Logo preview"
                                    className="w-9 h-9 rounded-lg border border-border object-contain bg-muted/30"
                                />
                            )}
                        </div>
                        {logoUploadError && <p className="text-xs text-destructive">{logoUploadError}</p>}
                        <p className="text-xs text-muted-foreground">
                            Shown on the chat launcher button. JPG, PNG, WebP, GIF, or SVG up to 5MB. Falls back to the icon above when no logo is set.
                        </p>
                    </div>

                    {/* Launcher Size */}
                    <div className="space-y-3">
                        <label className="text-sm font-semibold text-foreground">Launcher Size</label>
                        <div className="grid grid-cols-3 gap-3">
                            {BUTTON_SIZE_OPTIONS.map((opt) => (
                                <button
                                    key={opt.value}
                                    type="button"
                                    onClick={() => setThemeSettings({ ...themeSettings, chat_button_size: opt.value })}
                                    className={`px-4 py-3 rounded-lg border-2 text-sm flex flex-col items-center gap-2 transition-all ${
                                        themeSettings.chat_button_size === opt.value
                                            ? 'border-primary bg-primary/10 text-primary font-semibold'
                                            : 'border-border hover:border-primary/50 text-foreground'
                                    }`}
                                >
                                    <span
                                        className="rounded-full"
                                        style={{ width: opt.px * 0.4, height: opt.px * 0.4, backgroundColor: themeSettings.chat_button_color }}
                                    />
                                    {opt.name}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Teaser Bubble */}
                    <div className="space-y-3">
                        <label className="text-sm font-semibold text-foreground flex items-center gap-2">
                            <MessageCircle className="w-4 h-4" />
                            Teaser Bubble
                        </label>
                        <div className="flex items-center gap-3">
                            <button
                                type="button"
                                role="switch"
                                aria-checked={themeSettings.chat_teaser_enabled}
                                onClick={() => setThemeSettings({
                                    ...themeSettings,
                                    chat_teaser_enabled: !themeSettings.chat_teaser_enabled,
                                })}
                                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                                    themeSettings.chat_teaser_enabled ? 'bg-primary' : 'bg-muted'
                                }`}
                            >
                                <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                                    themeSettings.chat_teaser_enabled ? 'translate-x-5' : 'translate-x-1'
                                }`} />
                            </button>
                            <span className="text-sm text-muted-foreground">
                                {themeSettings.chat_teaser_enabled ? 'Enabled' : 'Disabled'} – Show a message bubble near the collapsed launcher
                            </span>
                        </div>
                        {themeSettings.chat_teaser_enabled && (
                            <>
                                <input
                                    type="text"
                                    value={themeSettings.chat_teaser_message}
                                    onChange={(e) => setThemeSettings({ ...themeSettings, chat_teaser_message: e.target.value })}
                                    placeholder="Need help? We're online now."
                                    maxLength={200}
                                    className="w-full px-3 py-2 text-sm rounded-lg border-2 border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                                />
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="space-y-1.5">
                                        <label className="text-xs font-medium text-foreground">Bubble Background</label>
                                        <div className="flex items-center gap-2 bg-muted/30 border border-border rounded-lg p-2">
                                            <label htmlFor="teaser-bg-picker" className="relative cursor-pointer flex-shrink-0">
                                                <div className="w-9 h-9 rounded-lg border-2 border-white shadow" style={{ backgroundColor: themeSettings.chat_teaser_bg_color }} />
                                                <input
                                                    id="teaser-bg-picker"
                                                    type="color"
                                                    value={themeSettings.chat_teaser_bg_color}
                                                    onChange={(e) => setThemeSettings({ ...themeSettings, chat_teaser_bg_color: e.target.value })}
                                                    className="absolute inset-0 opacity-0 cursor-pointer"
                                                />
                                            </label>
                                            <input
                                                type="text"
                                                value={themeSettings.chat_teaser_bg_color.replace('#', '')}
                                                onChange={(e) => {
                                                    const v = '#' + e.target.value.replace('#', '');
                                                    if (/^#[0-9A-Fa-f]{0,6}$/.test(v)) setThemeSettings({ ...themeSettings, chat_teaser_bg_color: v });
                                                }}
                                                className="flex-1 min-w-0 px-2 py-1.5 text-xs font-mono bg-background border border-border rounded-lg uppercase"
                                                maxLength={6}
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-xs font-medium text-foreground">Bubble Text Color</label>
                                        <div className="flex items-center gap-2 bg-muted/30 border border-border rounded-lg p-2">
                                            <label htmlFor="teaser-text-picker" className="relative cursor-pointer flex-shrink-0">
                                                <div className="w-9 h-9 rounded-lg border-2 border-white shadow" style={{ backgroundColor: themeSettings.chat_teaser_text_color }} />
                                                <input
                                                    id="teaser-text-picker"
                                                    type="color"
                                                    value={themeSettings.chat_teaser_text_color}
                                                    onChange={(e) => setThemeSettings({ ...themeSettings, chat_teaser_text_color: e.target.value })}
                                                    className="absolute inset-0 opacity-0 cursor-pointer"
                                                />
                                            </label>
                                            <input
                                                type="text"
                                                value={themeSettings.chat_teaser_text_color.replace('#', '')}
                                                onChange={(e) => {
                                                    const v = '#' + e.target.value.replace('#', '');
                                                    if (/^#[0-9A-Fa-f]{0,6}$/.test(v)) setThemeSettings({ ...themeSettings, chat_teaser_text_color: v });
                                                }}
                                                className="flex-1 min-w-0 px-2 py-1.5 text-xs font-mono bg-background border border-border rounded-lg uppercase"
                                                maxLength={6}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                        <p className="text-xs text-muted-foreground">
                            Disabled by default. When enabled, this message appears next to the launcher until it's opened.
                        </p>
                    </div>

                    {/* Glow Effect */}
                    <div className="space-y-3">
                        <label className="text-sm font-semibold text-foreground flex items-center gap-2">
                            <Sparkles className="w-4 h-4" />
                            Glow Effect
                        </label>
                        <div className="flex items-center gap-3">
                            <button
                                type="button"
                                role="switch"
                                aria-checked={themeSettings.chat_glow_enabled}
                                onClick={() => setThemeSettings({
                                    ...themeSettings,
                                    chat_glow_enabled: !themeSettings.chat_glow_enabled,
                                })}
                                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                                    themeSettings.chat_glow_enabled ? 'bg-primary' : 'bg-muted'
                                }`}
                            >
                                <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                                    themeSettings.chat_glow_enabled ? 'translate-x-5' : 'translate-x-1'
                                }`} />
                            </button>
                            <span className="text-sm text-muted-foreground">
                                {themeSettings.chat_glow_enabled ? 'Enabled' : 'Disabled'} – Animated pulsing glow ring around the launcher
                            </span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                            Disabled by default. Draws extra attention to the launcher button with a soft pulsing ring.
                        </p>
                    </div>

                    {/* Header Subtitle */}
                    <div className="space-y-3">
                        <label className="text-sm font-semibold text-foreground">Header Subtitle</label>
                        <input
                            type="text"
                            value={themeSettings.chat_header_subtitle}
                            onChange={(e) => setThemeSettings({ ...themeSettings, chat_header_subtitle: e.target.value })}
                            placeholder="We're here to help"
                            maxLength={100}
                            className="w-full px-3 py-2 text-sm rounded-lg border-2 border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                        />
                        <p className="text-xs text-muted-foreground">
                            Shown below the agent name in the chat widget header. Leave blank to use the default.
                        </p>
                    </div>

                    {/* Greeting Message (like voice AI) */}
                    <div className="space-y-3">
                        <label className="text-sm font-semibold text-foreground flex items-center gap-2">
                            <MessageCircle className="w-4 h-4" />
                            Greeting Message
                        </label>
                        <div className="flex items-center gap-3">
                            <button
                                type="button"
                                role="switch"
                                aria-checked={themeSettings.chat_greeting_enabled}
                                onClick={() => setThemeSettings({ 
                                    ...themeSettings, 
                                    chat_greeting_enabled: !themeSettings.chat_greeting_enabled 
                                })}
                                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                                    themeSettings.chat_greeting_enabled ? 'bg-primary' : 'bg-muted'
                                }`}
                            >
                                <span
                                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                        themeSettings.chat_greeting_enabled ? 'translate-x-5' : 'translate-x-1'
                                    }`}
                                />
                            </button>
                            <span className="text-sm text-muted-foreground">
                                {themeSettings.chat_greeting_enabled ? 'Enabled' : 'Disabled'} – Show greeting when widget opens
                            </span>
                        </div>
                        {themeSettings.chat_greeting_enabled && (
                            <div className="mt-3 space-y-3">
                                <textarea
                                    value={themeSettings.chat_greeting_message}
                                    onChange={(e) => setThemeSettings({ 
                                        ...themeSettings, 
                                        chat_greeting_message: e.target.value 
                                    })}
                                    placeholder="e.g., Hi! How can I help you today?"
                                    rows={3}
                                    className="w-full px-3 py-2 text-sm rounded-lg border-2 border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                                />
                                <div className="flex items-center gap-3">
                                    <button
                                        type="button"
                                        role="switch"
                                        aria-checked={themeSettings.chat_greeting_dynamic}
                                        onClick={() => setThemeSettings({ 
                                            ...themeSettings, 
                                            chat_greeting_dynamic: !themeSettings.chat_greeting_dynamic 
                                        })}
                                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                                            themeSettings.chat_greeting_dynamic ? 'bg-primary' : 'bg-muted'
                                        }`}
                                    >
                                        <span
                                            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                                themeSettings.chat_greeting_dynamic ? 'translate-x-5' : 'translate-x-1'
                                            }`}
                                        />
                                    </button>
                                    <span className="text-sm text-muted-foreground">
                                        Dynamic – {themeSettings.chat_greeting_dynamic ? 'On' : 'Off'} – {themeSettings.chat_greeting_dynamic ? 'Generate a new greeting each time (similar to above)' : 'Show the same static message every time'}
                                    </span>
                                </div>
                                <p className="text-xs text-muted-foreground">
                                    {themeSettings.chat_greeting_dynamic
                                        ? 'When enabled, the AI generates a fresh greeting each time the widget opens, using your message as a template for tone and style.'
                                        : 'This message will appear as the first AI message when a visitor opens the chat widget.'}
                                </p>
                            </div>
                        )}
                        <p className="text-xs text-muted-foreground">
                            Disabled by default. When enabled, the AI will greet visitors when they open the chat.
                        </p>
                    </div>

                    {/* Chat Inactivity Reminder */}
                    <div className="space-y-3">
                        <label className="text-sm font-semibold text-foreground flex items-center gap-2">
                            <Clock className="w-4 h-4" />
                            Inactivity Reminder
                        </label>
                        <div className="flex items-center gap-3">
                            <button
                                type="button"
                                role="switch"
                                aria-checked={themeSettings.chat_reminder_enabled}
                                onClick={() => setThemeSettings({ 
                                    ...themeSettings, 
                                    chat_reminder_enabled: !themeSettings.chat_reminder_enabled 
                                })}
                                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                                    themeSettings.chat_reminder_enabled ? 'bg-primary' : 'bg-muted'
                                }`}
                            >
                                <span
                                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                        themeSettings.chat_reminder_enabled ? 'translate-x-5' : 'translate-x-1'
                                    }`}
                                />
                            </button>
                            <span className="text-sm text-muted-foreground">
                                {themeSettings.chat_reminder_enabled ? 'Enabled' : 'Disabled'} – Send a reminder when user is inactive
                            </span>
                        </div>
                        {themeSettings.chat_reminder_enabled && (
                            <div className="mt-3 space-y-4 pl-1 border-l-2 border-primary/20 ml-1">
                                <div className="pl-4 space-y-4">
                                    {/* Inactivity timer */}
                                    <div>
                                        <label className="block text-xs font-medium text-foreground mb-1">
                                            Inactivity Timer (seconds)
                                        </label>
                                        <input
                                            type="number"
                                            min="5"
                                            max="300"
                                            value={themeSettings.chat_reminder_seconds}
                                            onChange={(e) => setThemeSettings({ 
                                                ...themeSettings, 
                                                chat_reminder_seconds: Math.max(5, Math.min(300, parseInt(e.target.value) || 30))
                                            })}
                                            className="w-full px-3 py-2 text-sm rounded-lg border-2 border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                                        />
                                        <p className="text-xs text-muted-foreground mt-1">
                                            Send reminder after {themeSettings.chat_reminder_seconds} seconds of user inactivity (5–300s)
                                        </p>
                                    </div>

                                    {/* Reminder type */}
                                    <div>
                                        <label className="block text-xs font-medium text-foreground mb-1">
                                            Reminder Type
                                        </label>
                                        <div className="grid grid-cols-2 gap-2">
                                            {['static', 'prompt'].map((type) => (
                                                <button
                                                    key={type}
                                                    onClick={() => setThemeSettings({ ...themeSettings, chat_reminder_type: type })}
                                                    className={`px-3 py-2 rounded-lg border-2 transition-all capitalize text-sm ${
                                                        themeSettings.chat_reminder_type === type
                                                            ? 'border-primary bg-primary/10 text-primary font-semibold'
                                                            : 'border-border hover:border-primary/50 text-foreground'
                                                    }`}
                                                >
                                                    {type === 'static' ? 'Static Message' : 'AI Generated'}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Reminder message / prompt */}
                                    <div>
                                        <label className="block text-xs font-medium text-foreground mb-1">
                                            {themeSettings.chat_reminder_type === 'static' ? 'Reminder Message' : 'Reminder Prompt'}
                                        </label>
                                        <textarea
                                            value={themeSettings.chat_reminder_message}
                                            onChange={(e) => setThemeSettings({ 
                                                ...themeSettings, 
                                                chat_reminder_message: e.target.value 
                                            })}
                                            placeholder={themeSettings.chat_reminder_type === 'static' 
                                                ? 'e.g., Are you still there? Let me know if you need any help!' 
                                                : 'e.g., Generate a friendly follow-up message asking if the user is still there, based on the conversation so far.'
                                            }
                                            rows={3}
                                            className="w-full px-3 py-2 text-sm rounded-lg border-2 border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                                        />
                                        <p className="text-xs text-muted-foreground mt-1">
                                            {themeSettings.chat_reminder_type === 'static'
                                                ? 'This exact message will be sent as a reminder.'
                                                : 'The AI will use this prompt (along with the conversation context) to generate a reminder message.'}
                                        </p>
                                    </div>

                                    {/* Auto-close section */}
                                    <div className="pt-3 border-t border-border/50 space-y-3">
                                        <div className="flex items-center gap-3">
                                            <button
                                                type="button"
                                                role="switch"
                                                aria-checked={themeSettings.chat_auto_close_enabled}
                                                onClick={() => setThemeSettings({ 
                                                    ...themeSettings, 
                                                    chat_auto_close_enabled: !themeSettings.chat_auto_close_enabled 
                                                })}
                                                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                                                    themeSettings.chat_auto_close_enabled ? 'bg-primary' : 'bg-muted'
                                                }`}
                                            >
                                                <span
                                                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                                        themeSettings.chat_auto_close_enabled ? 'translate-x-5' : 'translate-x-1'
                                                    }`}
                                                />
                                            </button>
                                            <span className="text-sm text-muted-foreground">
                                                <XCircle className="w-3.5 h-3.5 inline mr-1" />
                                                Auto-close session after reminder
                                            </span>
                                        </div>

                                        {themeSettings.chat_auto_close_enabled && (
                                            <div className="space-y-3 pl-4">
                                                <div>
                                                    <label className="block text-xs font-medium text-foreground mb-1">
                                                        Close After (seconds)
                                                    </label>
                                                    <input
                                                        type="number"
                                                        min="5"
                                                        max="300"
                                                        value={themeSettings.chat_auto_close_seconds}
                                                        onChange={(e) => setThemeSettings({ 
                                                            ...themeSettings, 
                                                            chat_auto_close_seconds: Math.max(5, Math.min(300, parseInt(e.target.value) || 30))
                                                        })}
                                                        className="w-full px-3 py-2 text-sm rounded-lg border-2 border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                                                    />
                                                    <p className="text-xs text-muted-foreground mt-1">
                                                        If the user doesn't respond within {themeSettings.chat_auto_close_seconds}s after the reminder, close the session
                                                    </p>
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-medium text-foreground mb-1">
                                                        Close Message (optional)
                                                    </label>
                                                    <textarea
                                                        value={themeSettings.chat_auto_close_message}
                                                        onChange={(e) => setThemeSettings({ 
                                                            ...themeSettings, 
                                                            chat_auto_close_message: e.target.value 
                                                        })}
                                                        placeholder="e.g., This chat session has been closed due to inactivity. Feel free to start a new conversation anytime!"
                                                        rows={2}
                                                        className="w-full px-3 py-2 text-sm rounded-lg border-2 border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                        <p className="text-xs text-muted-foreground">
                            Send a reminder message when the user hasn't responded for a set time, and optionally auto-close the session.
                        </p>
                    </div>

                    {/* Chat End Detection */}
                    <div className="space-y-3">
                        <label className="text-sm font-semibold text-foreground flex items-center gap-2">
                            <LogOut className="w-4 h-4" />
                            Chat End Detection
                        </label>
                        <div className="flex items-center gap-3">
                            <button
                                type="button"
                                role="switch"
                                aria-checked={themeSettings.chat_end_enabled}
                                onClick={() => setThemeSettings({
                                    ...themeSettings,
                                    chat_end_enabled: !themeSettings.chat_end_enabled,
                                })}
                                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                                    themeSettings.chat_end_enabled ? 'bg-primary' : 'bg-muted'
                                }`}
                            >
                                <span
                                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                        themeSettings.chat_end_enabled ? 'translate-x-5' : 'translate-x-1'
                                    }`}
                                />
                            </button>
                            <span className="text-sm text-muted-foreground">
                                {themeSettings.chat_end_enabled ? 'Enabled' : 'Disabled'} – Show a confirmation popup when user says goodbye
                            </span>
                        </div>
                        {themeSettings.chat_end_enabled && (
                            <div className="mt-3 space-y-4 pl-1 border-l-2 border-primary/20 ml-1">
                                <div className="pl-4 space-y-4">
                                    <div>
                                        <label className="block text-xs font-medium text-foreground mb-1">
                                            Trigger Phrases
                                        </label>
                                        <textarea
                                            value={themeSettings.chat_end_triggers}
                                            onChange={(e) => setThemeSettings({
                                                ...themeSettings,
                                                chat_end_triggers: e.target.value,
                                            })}
                                            placeholder={'bye\ngoodbye\nsee you\ntake care\nlater'}
                                            rows={4}
                                            className="w-full px-3 py-2 text-sm rounded-lg border-2 border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary font-mono"
                                        />
                                        <p className="text-xs text-muted-foreground mt-1">
                                            One phrase per line or comma-separated. Case insensitive. Leave empty to use defaults: bye, goodbye, see you, take care, later.
                                        </p>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-foreground mb-1">
                                            Confirmation Message
                                        </label>
                                        <input
                                            type="text"
                                            value={themeSettings.chat_end_confirm_message}
                                            onChange={(e) => setThemeSettings({
                                                ...themeSettings,
                                                chat_end_confirm_message: e.target.value,
                                            })}
                                            placeholder="Do you want to end the chat?"
                                            className="w-full px-3 py-2 text-sm rounded-lg border-2 border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                                        />
                                        <p className="text-xs text-muted-foreground mt-1">
                                            Shown in the popup when a trigger phrase is detected. Leave empty to use default.
                                        </p>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-foreground mb-1">
                                            Close Message (optional)
                                        </label>
                                        <textarea
                                            value={themeSettings.chat_end_close_message}
                                            onChange={(e) => setThemeSettings({
                                                ...themeSettings,
                                                chat_end_close_message: e.target.value,
                                            })}
                                            placeholder="This chat has ended. Feel free to start a new conversation anytime!"
                                            rows={2}
                                            className="w-full px-3 py-2 text-sm rounded-lg border-2 border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                                        />
                                        <p className="text-xs text-muted-foreground mt-1">
                                            Shown as a final message in the chat after the user confirms ending. Leave empty to use default.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                        <p className="text-xs text-muted-foreground">
                            Disabled by default. When enabled, the widget detects goodbye phrases and asks the user to confirm before ending the session.
                        </p>
                    </div>

                    {/* Image Sharing */}
                    <ChatImageSharingSetting
                        Icon={ImagePlus}
                        enabled={themeSettings.chat_image_upload_enabled}
                        onChange={(value) => setThemeSettings({ ...themeSettings, chat_image_upload_enabled: value })}
                        llmModel={agent?.llm_model}
                    />

                    {/* Preview Section */}
                    <div className="bg-muted/30 rounded-lg p-4 border border-border">
                        <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                            <Eye className="w-4 h-4" />
                            Preview
                        </h3>
                        <div className="flex items-center justify-center gap-4 p-6 bg-background rounded-lg">
                            <div className="text-center">
                                <p className="text-xs text-muted-foreground mb-2">Chat Button</p>
                                {themeSettings.chat_teaser_enabled && (
                                    <div
                                        className="mb-2 px-3 py-1.5 rounded-lg text-xs shadow-sm max-w-[160px] mx-auto"
                                        style={{ backgroundColor: themeSettings.chat_teaser_bg_color, color: themeSettings.chat_teaser_text_color }}
                                    >
                                        {themeSettings.chat_teaser_message}
                                    </div>
                                )}
                                <div className="relative inline-flex items-center justify-center">
                                    {themeSettings.chat_glow_enabled && (
                                        <span
                                            className="absolute inset-0 rounded-full animate-ping"
                                            style={{ backgroundColor: themeSettings.chat_button_color, opacity: 0.5 }}
                                        />
                                    )}
                                    <button
                                        className="relative rounded-full shadow-lg flex items-center justify-center transition-transform hover:scale-105 overflow-hidden"
                                        style={{
                                            backgroundColor: themeSettings.chat_button_color,
                                            width: BUTTON_SIZE_OPTIONS.find(s => s.value === themeSettings.chat_button_size)?.px || 56,
                                            height: BUTTON_SIZE_OPTIONS.find(s => s.value === themeSettings.chat_button_size)?.px || 56,
                                        }}
                                    >
                                        {themeSettings.chat_button_logo_url ? (
                                            <img src={themeSettings.chat_button_logo_url} alt="Logo" style={{ width: '82%', height: '82%', objectFit: 'contain' }} />
                                        ) : (
                                            React.createElement(getIconComponent(themeSettings.chat_button_icon), {
                                                className: "w-6 h-6 text-white"
                                            })
                                        )}
                                    </button>
                                </div>
                            </div>
                            <div className="text-center">
                                <p className="text-xs text-muted-foreground mb-2">Widget Header / Buttons</p>
                                <div
                                    className="px-6 py-3 rounded-lg shadow-md text-white font-medium"
                                    style={{ backgroundColor: themeSettings.chat_button_color }}
                                >
                                    Chat with us
                                </div>
                            </div>
                        </div>
                    </div>
                    </>
                    )}
                </div>

                {/* Footer */}
                <div className="border-t border-border p-4 bg-muted/30 flex justify-end gap-3">
                    <button
                        onClick={handlePreview}
                        className="px-4 py-2 bg-muted hover:bg-muted/80 text-foreground rounded-lg transition-colors flex items-center gap-2"
                    >
                        <Eye className="w-4 h-4" />
                        Live Preview
                    </button>
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-muted hover:bg-muted/80 text-foreground rounded-lg transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={isSaving}
                        className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
                    >
                        {isSaving ? (
                            <>
                                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                Saving...
                            </>
                        ) : (
                            'Save Changes'
                        )}
                    </button>
                </div>
            </div>
            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(null)}
                />
            )}
        </div>
    );
};

export default ChatThemeCustomizer;

